import { supabase } from '../lib/supabase';
import { SponsorStory, CreateSponsorStoryData, SponsorStoryStats } from '../types/models/sponsorStory';

class SponsorStoryService {
  /**
   * Create a new sponsor story
   */
  async searchUsers(query: string): Promise<Array<{ id: string; uid: string; displayName: string; photoURL: string | null }>> {
    if (!query.trim()) return [];
    const { data, error } = await supabase
      .from('users')
      .select('id, uid, display_name, photo_url')
      .ilike('display_name', `%${query.trim()}%`)
      .limit(8);
    if (error) throw error;
    return (data || []).map((u: any) => ({
      id: u.id,
      uid: u.uid,
      displayName: u.display_name,
      photoURL: u.photo_url,
    }));
  }

  async createStory(data: CreateSponsorStoryData, adminName: string): Promise<string> {
    try {
      const { data: story, error } = await supabase
        .from('sponsor_stories')
        .insert({
          athlete_name: data.athleteName.trim(),
          athlete_user_id: data.athleteUserId || null,
          sport: data.sport,
          sponsor_brand: data.sponsorBrand.trim(),
          deal_description: data.dealDescription.trim(),
          cover_image_url: data.coverImageUrl?.trim() || null,
          athlete_image_url: data.athleteImageUrl?.trim() || null,
          deal_amount: data.dealAmount?.trim() || null,
          location: data.location?.trim() || null,
          is_featured: data.isFeatured || false,
          author_name: adminName,
          published_at: data.publishedAt
            ? new Date(data.publishedAt).toISOString()
            : new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      return story.id;
    } catch (error) {
      console.error('Error creating sponsor story:', error);
      throw new Error('Failed to create sponsor story');
    }
  }

  /**
   * Get all sponsor stories (for admin dashboard)
   */
  async getAllStories(): Promise<SponsorStory[]> {
    try {
      const { data, error } = await supabase
        .from('sponsor_stories')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching sponsor stories:', error);
      throw new Error('Failed to fetch sponsor stories');
    }
  }

  /**
   * Get active stories (for app display)
   */
  async getActiveStories(): Promise<SponsorStory[]> {
    try {
      const { data, error } = await supabase
        .from('sponsor_stories')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('published_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching active sponsor stories:', error);
      throw new Error('Failed to fetch active sponsor stories');
    }
  }

  /**
   * Update an existing sponsor story
   */
  async updateStory(storyId: string, updates: Partial<CreateSponsorStoryData>): Promise<void> {
    try {
      const dbUpdates: Record<string, any> = {};

      if (updates.athleteName !== undefined) dbUpdates.athlete_name = updates.athleteName.trim();
      if (updates.athleteUserId !== undefined) dbUpdates.athlete_user_id = updates.athleteUserId || null;
      if (updates.sport !== undefined) dbUpdates.sport = updates.sport;
      if (updates.sponsorBrand !== undefined) dbUpdates.sponsor_brand = updates.sponsorBrand.trim();
      if (updates.dealDescription !== undefined) dbUpdates.deal_description = updates.dealDescription.trim();
      if (updates.coverImageUrl !== undefined) dbUpdates.cover_image_url = updates.coverImageUrl?.trim() || null;
      if (updates.athleteImageUrl !== undefined) dbUpdates.athlete_image_url = updates.athleteImageUrl?.trim() || null;
      if (updates.dealAmount !== undefined) dbUpdates.deal_amount = updates.dealAmount?.trim() || null;
      if (updates.location !== undefined) dbUpdates.location = updates.location?.trim() || null;
      if (updates.isFeatured !== undefined) dbUpdates.is_featured = updates.isFeatured;
      if (updates.publishedAt !== undefined) {
        dbUpdates.published_at = updates.publishedAt
          ? new Date(updates.publishedAt).toISOString()
          : null;
      }

      const { error } = await supabase
        .from('sponsor_stories')
        .update(dbUpdates)
        .eq('id', storyId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating sponsor story:', error);
      throw new Error('Failed to update sponsor story');
    }
  }

  /**
   * Delete a sponsor story
   */
  async deleteStory(storyId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('sponsor_stories')
        .delete()
        .eq('id', storyId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting sponsor story:', error);
      throw new Error('Failed to delete sponsor story');
    }
  }

  /**
   * Toggle active status
   */
  async toggleActive(storyId: string, isActive: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('sponsor_stories')
        .update({ is_active: isActive })
        .eq('id', storyId);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling story status:', error);
      throw new Error('Failed to update story status');
    }
  }

  /**
   * Toggle featured status
   */
  async toggleFeatured(storyId: string, isFeatured: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('sponsor_stories')
        .update({ is_featured: isFeatured })
        .eq('id', storyId);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw new Error('Failed to update featured status');
    }
  }

  /**
   * Upload an image file to Supabase Storage and return its public URL.
   * @param file  The File object from a browser file input
   * @param type  'cover' | 'athlete' — determines the storage sub-folder
   */
  async uploadImage(file: File, type: 'cover' | 'athlete'): Promise<string> {
    const ext = file.name.split('.').pop() ?? 'jpg';
    const path = `${type}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage
      .from('sponsor-stories')
      .upload(path, file, { contentType: file.type, upsert: false });

    if (error) {
      console.error('Storage upload error:', error);
      throw new Error('Failed to upload image');
    }

    const { data } = supabase.storage.from('sponsor-stories').getPublicUrl(path);
    return data.publicUrl;
  }

  /**
   * Get sponsor story statistics
   */
  async getStats(): Promise<SponsorStoryStats> {
    try {
      const stories = await this.getAllStories();
      const stats: SponsorStoryStats = { total: 0, active: 0, featured: 0 };
      stories.forEach((s) => {
        stats.total++;
        if (s.isActive) stats.active++;
        if (s.isFeatured) stats.featured++;
      });
      return stats;
    } catch (error) {
      console.error('Error calculating stats:', error);
      throw new Error('Failed to calculate stats');
    }
  }

  /**
   * Map database row to SponsorStory model
   */
  private mapToModel(data: any): SponsorStory {
    return {
      id: data.id,
      athleteName: data.athlete_name,
      athleteUserId: data.athlete_user_id || undefined,
      sport: data.sport,
      sponsorBrand: data.sponsor_brand,
      dealDescription: data.deal_description,
      coverImageUrl: data.cover_image_url,
      athleteImageUrl: data.athlete_image_url,
      dealAmount: data.deal_amount,
      location: data.location,
      isActive: data.is_active,
      isFeatured: data.is_featured,
      authorName: data.author_name || 'Admin',
      publishedAt: data.published_at,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const sponsorStoryService = new SponsorStoryService();
export default sponsorStoryService;
